import { useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { derDashboardInfiniteQueryOptions, downloadDerDashboard } from '../api/dashboards.api';
import { ExportButton } from '@/components/common/ExportButton';
import { getDerDashboardColumns } from '../components/DerDashboardColumns';
import { ReportingPeriodFilterPanel } from '../components/ReportingPeriodFilterPanel';
import { useReportingPeriodFilters } from '../hooks/useReportingPeriodFilters';

const TOP_EXPENSE_TYPES = 5;
const STAT_CARD_COUNT = TOP_EXPENSE_TYPES + 1;

export function DerDashboardPage() {
  const controller = useReportingPeriodFilters('der');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(derDashboardInfiniteQueryOptions(controller.params));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const expenseTypes = useMemo(() => firstPageMeta?.columns ?? [], [firstPageMeta]);
  const columns = useMemo(() => getDerDashboardColumns(expenseTypes), [expenseTypes]);

  const totals = useMemo<LabeledStat[]>(() => {
    if (!firstPageMeta) return [];

    const byType = firstPageMeta.totals.byExpenseType;
    const top = [...expenseTypes]
      .map((expenseType) => ({
        key: String(expenseType.id),
        label: expenseType.code ?? expenseType.name,
        value: byType[String(expenseType.id)] ?? 0,
        hint: expenseType.name,
        unit: TAKA_SYMBOL,
      }))
      .filter((entry) => entry.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, TOP_EXPENSE_TYPES);

    return [
      {
        key: 'total',
        label: 'Total',
        value: firstPageMeta.totals.total,
        unit: TAKA_SYMBOL,
      },
      ...top,
    ];
  }, [firstPageMeta, expenseTypes]);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DER Dashboard"
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              onExport={() => downloadDerDashboard(controller.params)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <ReportingPeriodFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the dashboard"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            stats={totals}
            isLoading={isLoading}
            isFetching={isRefetching}
            skeletonCardCount={STAT_CARD_COUNT}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="No expenses for these filters"
            emptyHint="Try a wider date range."
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
